import React, { useEffect, useState } from 'react';
import { TagType, TemplateScopeType, TemplateType } from '@dev/ee-api/ee-api-models';
import { push as Push } from 'redux-first-history';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { filter } from 'lodash-es';
import { TemplateList as TemplateListModule } from '@dev/ee-modules';

import { TemplatesPaths } from '@/services/dictionaries/Paths';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import {
    getTemplateList,
    StartLoadingTemplateParams,
    StartLoadingTemplates,
} from '@/store/templateList/actions';
import { getTemplateTagsList, templateTagListStart } from '@/store/templateTagList/actions';
import { getConfigStore } from '@/store';
import { getAccountStore } from '@/store/account/actions';
import { GetDashboardSettings, getDashboardSettingsStore } from '@/store/dashboardSettings/actions';
import { ApplicationState } from '@/store';
import { PreviewContent } from '@/components/LandingPageList/LandingPageListInterface';
import { getLandingPageList, TemplatePreviewStart } from '@/store/landingPageList/actions';
import { HistoryBackAction } from '@/services/hooks/useHistoryBackAction';
import { PreviewMultiDoubleOptInTemplate } from '../Preview';
import { ModalStateAction, setActivationTemplateAction } from '../../../../state';

interface FormMultiDoubleOptInTemplatesGalleryListProps {
    dispatchModalState: React.Dispatch<ModalStateAction<any>>,
    historyBackAction?: HistoryBackAction
}

export const FormMultiDoubleOptInTemplatesGalleryList = ({ dispatchModalState, historyBackAction }: FormMultiDoubleOptInTemplatesGalleryListProps): JSX.Element => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const [openPreview, setOpenPreview] = useState(false);
    const [previewContent, setPreviewContent] = useState<PreviewContent>(null);

    const config = useSelector(getConfigStore);
    const account = useSelector(getAccountStore);
    const browser = useSelector((state: ApplicationState) => state.browser);
    const templates = useSelector(getTemplateList);
    const tags = useSelector(getTemplateTagsList);
    const tagListStart = (type: TagType) => dispatch(templateTagListStart(type));
    const dashboardSettings = useSelector(getDashboardSettingsStore);
    const { template, name, PreviewLoading } = useSelector(getLandingPageList);

    useEffect(() => {
        if (PreviewLoading) {
            return;
        }
        setPreviewContent({
            name,
            content: template as string,
        });
    },[PreviewLoading]);

    useEffect(() => {
        historyBackAction?.pushBackCallback(closePreview)
    }, []);

    const closePreview = (): boolean => {
        setPreviewContent(null);
        setOpenPreview(false);
        return true;
    };

    const clickCloseButton = (): void => {
        historyBackAction.popAndExecuteBackCallback();
    }

    const startLoadingTemplates = (params: StartLoadingTemplateParams) => {
        const newTemplateType = filter(params.templateTypes, (templateType:TemplateType) => templateType !== TemplateType.DragDropEditor);
        dispatch(
            StartLoadingTemplates({
                ...params,
                type: [TemplateScopeType.Personal],
                templateTypes: newTemplateType || [TemplateType.TemplateEditor, TemplateType.RawHTML],
            })
        );
    }
    const showDialog = (params: ShowDialogParams) => dispatch(ShowDialog(params));
    const push = (url: string) => dispatch(Push(url));

    const previewTemplateFromGallery = () => {
        setOpenPreview(true);
    };

    const selectTemplateFromGallery = (templateName: string) => {
        // sendEventToGTM("select-landing-page-template", { templateName });
        const screenshot = templates?.templatesList?.find((template) => template.Name === templateName)?.Screenshot;
        dispatchModalState(setActivationTemplateAction(screenshot, templateName));
    };

    const templatePreviewStart = (templateName: string) => {
        dispatch(TemplatePreviewStart(templateName));
    };

    const galleryScrollContainerId = 'galleryScrollContainer';
    const galleryScrollContainerSelector = `#${galleryScrollContainerId} .MuiDialog-paperScrollBody`;

    return (
        <>
            <TemplateListModule
                paths={TemplatesPaths}
                t={t}
                config={config}
                account={account}
                dashboardSettings={dashboardSettings}
                browser={browser}
                templates={templates}
                tags={tags}
                location={location}
                GalleryTemplatePreview={previewTemplateFromGallery}
                SelectTemplate={selectTemplateFromGallery}
                StartLoadingTemplates={startLoadingTemplates}
                ShowDialog={showDialog}
                GetDashboardSettings={GetDashboardSettings}
                Push={push}
                templateTagListStart={tagListStart}
                TemplatePreviewStart={templatePreviewStart}
                lazyLoadScrollContainer={galleryScrollContainerSelector}
                isMultiDoubleOptInTemplatesGallery
                APIPATH={APIPATH}
                isGallery
                isMarketing
            />
            {open && !PreviewLoading && (
                <PreviewMultiDoubleOptInTemplate
                    open={openPreview}
                    name={previewContent?.name}
                    previewContent={previewContent?.content}
                    onClose={clickCloseButton}
                />
            )}
        </>
    );
};
